import React, { FC } from 'react'
import { ID } from '@/_metronic/helpers'
import { useState, useEffect } from 'react'
import { TrxCarrierData } from '../core/_models'
import { getTrxCarrier } from '../core/_requests'
import { DonutCard } from './DonutCard'
import { useIntl } from 'react-intl'

type Props = {
  userId?: ID
  from: Date
  to: Date
  timezone: string;
  height: number
  paymode?: string
}

const decodeHtmlEntities = (str: string): string => {
  return str.replace(/&#x([0-9A-Fa-f]+);/g, (_, hex) =>
    String.fromCharCode(parseInt(hex, 16))
  ).replace(/&#([0-9]+);/g, (_, dec) =>
    String.fromCharCode(parseInt(dec, 10))
  );
}

const normalizeData = (data: TrxCarrierData[]) => {
  const normalized = data.reduce((acc, item) => {
    const decodedCarrier = decodeHtmlEntities(item.carrier ?? 'Unknown');
    const carrier = decodedCarrier === 'Société française du radiotéléphone' ? 'SFR' : decodedCarrier;
    const existing = acc.find(e => e.carrier === carrier);
    if (existing) {
      existing.trx += item.trx;
      existing.paid += item.paid;
      existing.payouts += item.payouts;
    } else {
      acc.push({ ...item, carrier });
    }
    return acc;
  }, [] as TrxCarrierData[]);

  const limit = 5;
  const top = normalized.slice(0, limit);
  const others = normalized.slice(limit).reduce((acc, item) => {
    acc.trx += item.trx;
    acc.paid += item.paid;
    acc.payouts += item.payouts;
    return acc;
  }, { carrier: 'Others', trx: 0, paid: 0, payouts: 0 });

  return normalized.length > limit ? [...top, others] : top;
}

const TrxCarrierChart: FC<Props> = ({ userId, from, to, height, paymode, timezone }) => {
  const [data, setData] = useState<Array<TrxCarrierData>>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const intl = useIntl()

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true)
        const res = await getTrxCarrier({ from, to, userId, paymode, timezone })
          .then((data: TrxCarrierData[]) => normalizeData(data))

        setData(res || [])
      } catch (e: unknown) {
        console.log(e)
      } finally {
        setIsLoading(false)
      }
    }
    load()
  }, [userId, from, to, paymode])

  return (
    <DonutCard<TrxCarrierData>
      title={
        <>
          Customer carriers
          {paymode && (
            <i className="text-danger ms-1">
              {intl.formatMessage({ id: `P2C.PAYMODE_${paymode.toUpperCase()}` })}
            </i>
          )}
        </>
      }
      id='carriers'
      showAggs={true}
      data={data}
      height={height}
      isLoading={isLoading}
      getLabel={(item) => item.carrier}
      getAggValue={(item, agg) => Number(item[agg as keyof TrxCarrierData]) || 0}
    />
  )

}

export default TrxCarrierChart
